import { Box, Button, CircularProgress, IconButton, Paper, Stack, Switch, Tab, TablePagination, Tabs, Typography } from "@mui/material";
import Api from "api";
import { useApiClient, useAppDispatch, useAppSelector } from "app/hooks";
import { ErrorContext } from "components/contexts/error-handler";
import ConfirmDialog from "components/generic/confirm-dialog";
import { Metaform, MetaformField, MetaformFieldType, Reply, ReplyOrderCriteria, ReplySummary, ReplySummaryField, ScriptType } from "generated/client";
import strings from "localization/strings";
import moment from "moment";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { selectKeycloak } from "features/auth-slice";
import { FormContext, FeatureType, FeatureStrategy, ReplyStatus } from "types";
import AuthUtils from "utils/auth-utils";
import { setSnackbarMessage } from "features/snackbar-slice";
import LocalizationUtils from "utils/localization-utils";
import { CREATED_FIELD_NAME, MODIFIED_FIELD_NAME, STATUS_FIELD_NAME } from "consts";
import Feature from "components/containers/feature";
import FileUtils from "utils/file-utils";
import ScriptUtils from "utils/script-utils";

/**
 * Meta fields with type of date-time
 */
const DATETIME_META_FIELDS = [
  MODIFIED_FIELD_NAME,
  CREATED_FIELD_NAME
];

/**
 * Bar colors of a summary distribution, ordered from the first row to the last
 */
const DISTRIBUTION_COLORS = [ "#278062", "#4f9a7d", "#8cbf3f", "#ED8113", "#c0392b" ];

/**
 * Amount of free text answers shown before the reader expands the card
 */
const COLLAPSED_TEXT_ANSWERS = 5;

/**
 * Width the charted content is capped to, so that bars stay readable on a wide display
 */
const SUMMARY_MAX_WIDTH = 1280;

/**
 * Single row of a summary distribution
 */
interface DistributionRow {
  key: string;
  label: string;
  count: number;
}

/**
 * Form replies screen component
 */
const FormRepliesScreen: React.FC = () => {
  const errorContext = useContext(ErrorContext);
  const navigate = useNavigate();

  const apiClient = useApiClient(Api.getApiClient);
  const { repliesApi, metaformsApi, metaformStatisticsApi, scriptsApi } = apiClient;

  const dispatch = useAppDispatch();
  const keycloak = useAppSelector(selectKeycloak);

  const [ rows, setRows ] = useState<any[]>([]);
  const [ managementFields, setManagementFields ] = useState<MetaformField[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ summaryLoading, setSummaryLoading ] = useState(false);
  const [ replySummary, setReplySummary ] = useState<ReplySummary>();
  const [ metaform, setMetaform ] = useState<Metaform>();
  const [ deletableReplyId, setDeletableReplyId ] = useState<string | undefined>(undefined);
  const [ showAllReplies, setShowAllReplies ] = useState(false);
  const [ selectedTab, setSelectedTab ] = useState(0);
  const [ expandedTextFields, setExpandedTextFields ] = useState<string[]>([]);
  const resultsPerPage = 25;
  const [ page, setPage ] = useState(0);
  const [ totalResults, setTotalResults ] = useState(0);

  const useparams = useParams();
  const { formSlug } = useparams;

  if (!formSlug) {
    errorContext.setError(strings.errorHandling.adminRepliesScreen.formSlugNotFound);
  }

  /**
   * Return fields that include context "MANAGEMENT_LIST"
   *
   * @param metaformData metaform data
   */
  const getManagementListFields = (metaformData: Metaform) => {
    if (!metaformData) {
      return;
    }

    const fieldData = (metaformData.sections ?? [])
      .flatMap(section => section.fields ?? [])
      .filter(field => (field.contexts ?? []).includes(FormContext.MANAGEMENT_LIST));
    return fieldData;
  };

  /**
   * Builds a row for the table
   *
   * @param reply reply
   */
  const buildRow = (reply: Reply, managementListFields: MetaformField[]) => {
    const row : { [key: string]: string | number } = {};

    row.id = reply.id!;

    row.replyStatus = reply.data?.status?.toString() ?? "";

    managementListFields.forEach(field => {
      const replyData = reply.data;
      const fieldName = field.name;

      if (!replyData || !fieldName) {
        return;
      }

      const fieldValue = replyData[fieldName];

      if (!fieldValue) {
        return;
      }

      const fieldOptions = field.options || [];

      switch (field.type) {
        case MetaformFieldType.Select:
        case MetaformFieldType.Radio:
          row[fieldName] = fieldOptions.find(fieldOption => fieldOption.name === fieldValue.toString())?.text ?? fieldValue.toString();
          break;
        default:
          row[fieldName] = replyData[fieldName].toString();
      }
    });

    return row;
  };

  /**
   * Replies screen loadData
   */
  const loadData = async () => {
    if (!formSlug) {
      return;
    }

    setLoading(true);

    try {
      const metaformData = await metaformsApi.findMetaform({ metaformSlug: formSlug });
      setMetaform(metaformData);
      const [ [repliesData, headers], managementListFields ] = await Promise.all([
        repliesApi.listRepliesWithHeaders({
          metaformId: metaformData.id!,
          latestFirst: true,
          orderBy: ReplyOrderCriteria.Created,
          fields: [`${showAllReplies ? "" : "status:waiting"}`],
          firstResult: page * resultsPerPage,
          maxResults: resultsPerPage
        }),
        getManagementListFields(metaformData)
      ]);

      if (!repliesData || !managementListFields) {
        return;
      }
      const replyRows = repliesData.map(reply => buildRow(reply, managementListFields));

      setManagementFields(managementListFields);
      setTotalResults(Number(headers.get("Total-Results")));
      setRows(replyRows);
    } catch (e) {
      errorContext.setError(strings.errorHandling.adminRepliesScreen.fetchReplies, e);
    }

    setLoading(false);
  };

  /**
   * Loads aggregate response data for the summary view.
   */
  const loadReplySummary = async () => {
    if (!formSlug) {
      return;
    }

    setSummaryLoading(true);

    try {
      const metaformData = await metaformsApi.findMetaform({ metaformSlug: formSlug });
      setMetaform(metaformData);
      const summary = await metaformStatisticsApi.getReplySummary({ metaformId: metaformData.id! });
      setReplySummary(summary);
    } catch (e) {
      errorContext.setError(strings.errorHandling.adminRepliesScreen.fetchReplies, e);
    }

    setSummaryLoading(false);
  };

  useEffect(() => {
    if (selectedTab === 0) {
      loadReplySummary();
      return;
    }

    loadData();
  }, [selectedTab, showAllReplies, page]);

  /**
   * Deletes a reply
   *
   * @param replyId reply id
   */
  const deleteReply = async (replyId: string) => {
    setLoading(true);

    try {
      if (!metaform || !metaform.id || !replyId) {
        return;
      }

      await repliesApi.deleteReply({
        metaformId: metaform.id,
        replyId: replyId
      });

      dispatch(setSnackbarMessage(strings.successSnackbars.replies.replyDeleteSuccessText));
      setRows(rows?.filter(row => row.id !== replyId));
    } catch (e) {
      errorContext.setError(strings.errorHandling.adminRepliesScreen.deleteReply, e);
    }

    setLoading(false);
  };

  /**
   * Event handler for delete reply dialog confirm
   */
  const onReplyDeleteConfirm = async () => {
    if (deletableReplyId) {
      deleteReply(deletableReplyId);
    }

    setDeletableReplyId(undefined);
  };

  /**
   * Opens a response for a manager.
   *
   * @param replyId response ID
   */
  const openReply = (replyId: string) => {
    if (!AuthUtils.isMetaformManager(keycloak)) {
      return;
    }

    navigate(replyId);
  };

  /**
   * Event handler for export button click
   */
  const onExportClick = async () => {
    setLoading(true);

    try {
      if (!metaform?.id) {
        return;
      }

      // eslint-disable-next-line no-underscore-dangle
      const file = await repliesApi._export({ metaformId: metaform.id, format: "XLSX" });

      if (!metaform.scripts) {
        FileUtils.downloadBlob(file, "replies.xlsx");
        return;
      }
      
      const scripts = await Promise.all(metaform.scripts.map(script => scriptsApi.findScript({ scriptId: script })));
      const xlsxScripts = scripts.filter(script => script.type === ScriptType.ExportXlsx);

      let newFile = file;

      const nameClassifierEntries = metaform.sections?.map(section => section.fields)
        .flat().filter(field => !!field && !!field.classifiers && field.title!)
        .map(field => field!.classifiers!.map(classifier => ({ name: field!.title!, classifier: classifier })))
        .flat() ?? [];

      // eslint-disable-next-line no-restricted-syntax
      for (const script of xlsxScripts) {
        // eslint-disable-next-line no-await-in-loop
        newFile = await ScriptUtils.runScriptOnSpreadsheet(newFile, script.content, nameClassifierEntries);
      }

      FileUtils.downloadBlob(newFile, "replies.xlsx");
    } catch (e) {
      errorContext.setError(strings.errorHandling.adminRepliesScreen.export, e);
    }

    setLoading(false);
  };

  /**
   * Renders delete reply confirm dialog
   */
  const renderDeleteReplyConfirm = () => {
    return (
      <ConfirmDialog
        onClose={ () => setDeletableReplyId(undefined) }
        onCancel={ () => setDeletableReplyId(undefined) }
        onConfirm={ onReplyDeleteConfirm }
        cancelButtonText={ strings.generic.cancel }
        positiveButtonText={ strings.generic.confirm }
        title={ strings.repliesScreen.confirmDeleteReplyTitle }
        text={ strings.repliesScreen.confirmDeleteReplyText }
        open={ !!deletableReplyId }
      />
    );
  };

  /**
   * Shows or hides the free text answers of a field beyond the collapsed ones.
   *
   * @param fieldName summary field name
   */
  const toggleTextField = (fieldName: string) => {
    setExpandedTextFields(current => (current.includes(fieldName)
      ? current.filter(name => name !== fieldName)
      : [ ...current, fieldName ]));
  };

  /**
   * Resolves the bar color of a distribution row.
   *
   * The palette is spread over the row count so that both short option lists and wide numeric scales
   * run evenly from the first row to the last.
   *
   * @param index row index
   * @param rowCount total amount of rows
   */
  const getDistributionColor = (index: number, rowCount: number) => {
    const paletteIndex = Math.floor((index * DISTRIBUTION_COLORS.length) / Math.max(rowCount, 1));
    return DISTRIBUTION_COLORS[Math.min(paletteIndex, DISTRIBUTION_COLORS.length - 1)];
  };

  /**
   * Renders a distribution as labelled bars.
   *
   * @param rows distribution rows
   * @param total answer count the percentages are relative to
   */
  const renderDistribution = (distributionRows: DistributionRow[], total: number) => {
    const maxCount = Math.max(...distributionRows.map(row => row.count), 0);

    return (
      <Stack spacing={ 1.125 }>
        { distributionRows.map((row, index) => (
          <Box
            key={ row.key }
            sx={{
              alignItems: "center",
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "104px minmax(0, 1fr) 76px",
                sm: "180px minmax(0, 1fr) 88px"
              }
            }}
          >
            <Typography sx={{ color: "#4d4d4d", fontSize: 13 }}>{ row.label }</Typography>
            <Box sx={{
              backgroundColor: "#f2f4f3", borderRadius: 0.5, height: 20, overflow: "hidden"
            }}
            >
              <Box
                sx={{
                  backgroundColor: getDistributionColor(index, distributionRows.length),
                  height: "100%",
                  width: `${maxCount ? (row.count / maxCount) * 100 : 0}%`
                }}
              />
            </Box>
            <Typography
              sx={{
                color: "#4d4d4d",
                fontSize: 13,
                fontVariantNumeric: "tabular-nums",
                textAlign: "right"
              }}
            >
              { `${row.count} (${total ? Math.round((row.count / total) * 100) : 0}%)` }
            </Typography>
          </Box>
        )) }
      </Stack>
    );
  };

  /**
   * Renders a full width summary card.
   *
   * @param title card title
   * @param meta secondary text shown at the end of the title row
   * @param content card content
   */
  const renderSummaryCard = (title: string, meta: string, content: ReactNode) => (
    <Paper
      variant="outlined"
      sx={{
        borderColor: "#e2e5e3", borderRadius: 1, p: 2.5
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={ 1.5 } mb={ 1.75 }>
        <Typography sx={{
          color: "#1a1a1a", fontSize: 14.5, fontWeight: 600
        }}
        >
          { title }
        </Typography>
        <Typography sx={{
          color: "#8a8f8c", fontSize: 12.5, ml: "auto !important"
        }}
        >
          { meta }
        </Typography>
      </Stack>
      { content }
    </Paper>
  );

  /**
   * Renders the distribution card of a single summary field.
   *
   * @param field summary field
   */
  const renderFieldSummary = (field: ReplySummaryField) => {
    const answerCount = field.answerCount || 0;
    const options = field.options || [];
    const textAnswers = field.textAnswers || [];
    // numeric scales read best from the highest value down, which also keeps the palette aligned with them
    const numericScale = field.type === MetaformFieldType.Slider || field.type === MetaformFieldType.Number;
    const orderedOptions = numericScale ? [ ...options ].reverse() : options;
    const average = field.average === undefined || field.average === null
      ? undefined
      : `${field.average.toFixed(1)} ${strings.repliesScreen.average}`;

    const countLabel = textAnswers.length
      ? `${answerCount} ${strings.repliesScreen.openAnswers}`
      : `${answerCount} ${strings.repliesScreen.answers}`;
    const meta = [ countLabel, orderedOptions.length ? average : undefined ]
      .filter(part => part)
      .join(" · ");

    let content: ReactNode;
    if (textAnswers.length) {
      const expanded = expandedTextFields.includes(field.name || "");
      const visibleAnswers = expanded ? textAnswers : textAnswers.slice(0, COLLAPSED_TEXT_ANSWERS);

      content = (
        <Stack spacing={ 1.125 }>
          { visibleAnswers.map((answer, index) => (
            <Typography
              // free text answers carry no identifier of their own
              // eslint-disable-next-line react/no-array-index-key
              key={ `${field.name}-${index}` }
              sx={{
                backgroundColor: "#fafbfa",
                borderLeft: "2px solid #cfd6d2",
                borderRadius: "0 4px 4px 0",
                color: "#4d4d4d",
                fontSize: 13.5,
                lineHeight: 1.55,
                px: 1.75,
                py: 1.375
              }}
            >
              { answer }
            </Typography>
          )) }
          { expanded && answerCount > textAnswers.length &&
            <Typography sx={{ color: "#8a8f8c", fontSize: 12.5 }}>
              { strings.formatString(strings.repliesScreen.latestAnswersShown, textAnswers.length) }
            </Typography>
          }
          { textAnswers.length > COLLAPSED_TEXT_ANSWERS &&
            <Button
              onClick={ () => toggleTextField(field.name || "") }
              sx={{
                alignSelf: "flex-start",
                color: "#278062",
                fontSize: 13,
                fontWeight: 500,
                px: 0
              }}
            >
              { expanded
                ? strings.repliesScreen.showFewerAnswers
                : strings.formatString(
                  // the API caps what it returns, so only promise every answer when it actually returned them all
                  answerCount > textAnswers.length
                    ? strings.repliesScreen.showLatestAnswers
                    : strings.repliesScreen.showAllAnswers,
                  textAnswers.length
                ) }
            </Button>
          }
        </Stack>
      );
    } else if (orderedOptions.length) {
      content = renderDistribution(
        orderedOptions.map(option => ({
          key: option.value || "",
          label: option.label || option.value || "",
          count: option.count || 0
        })),
        answerCount
      );
    } else if (average) {
      content = (
        <Typography sx={{
          color: "#1a1a1a",
          fontSize: 26,
          fontWeight: 500
        }}
        >
          { average }
        </Typography>
      );
    } else {
      content = <Typography sx={{ color: "#8a8f8c", fontSize: 13 }}>{ strings.repliesScreen.noAnswers }</Typography>;
    }

    return (
      <Box key={ field.name }>
        { renderSummaryCard(field.title || field.name || "", meta, content) }
      </Box>
    );
  };

  /**
   * Renders the aggregate response summary.
   */
  const renderSummary = () => {
    if (summaryLoading) {
      return <Box display="flex" justifyContent="center" p={ 4 }><CircularProgress size={ 28 }/></Box>;
    }

    if (!replySummary) {
      return <Typography sx={{ color: "#8a8f8c", fontSize: 13 }}>{ strings.repliesScreen.noReplies }</Typography>;
    }

    const totalReplies = replySummary.totalReplies || 0;
    const unprocessedReplies = replySummary.unprocessedReplies || 0;
    const processedReplies = Math.max(totalReplies - unprocessedReplies, 0);
    const statuses = replySummary.statuses || [];
    // the status field is already covered by the status card above the questions
    const fields = (replySummary.fields || []).filter(field => field.name !== STATUS_FIELD_NAME);

    return (
      <Stack spacing={ 2 }>
        { /* the tiles carry no length encoded meaning, so they span the full width and anchor the top edge */ }
        <Box sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))"
        }}
        >
          { [
            [strings.repliesScreen.totalReplies, totalReplies],
            [strings.repliesScreen.openReplies, unprocessedReplies],
            [strings.repliesScreen.processedReplies, processedReplies]
          ].map(([label, value]) => (
            <Paper
              key={ label }
              variant="outlined"
              sx={{
                borderColor: "#e2e5e3", borderRadius: 1, p: 2.25
              }}
            >
              <Typography
                sx={{
                  color: "#8a8f8c",
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase"
                }}
              >
                { label }
              </Typography>
              <Typography sx={{
                color: "#1a1a1a", fontSize: 28, fontWeight: 300, mt: 0.75
              }}
              >
                { value }
              </Typography>
            </Paper>
          )) }
        </Box>
        { /* bars encode their value by length, so the charted content stays capped however wide the display is */ }
        <Stack spacing={ 2 } sx={{ maxWidth: SUMMARY_MAX_WIDTH }}>
          { !!statuses.length && renderSummaryCard(
            strings.repliesScreen.statusDistribution,
            `${totalReplies} ${strings.repliesScreen.answers}`,
            renderDistribution(
              statuses.map(status => ({
                key: status.status || "",
                label: LocalizationUtils.getLocalizedStatusOfReply(status.status as ReplyStatus) || status.status || "",
                count: status.count || 0
              })),
              totalReplies
            )
          ) }
          <Typography sx={{
            color: "#1a1a1a", fontSize: 16, fontWeight: 600, pt: 0.5
          }}
          >
            { strings.repliesScreen.distributions }
          </Typography>
          { !fields.length && <Typography sx={{ color: "#8a8f8c", fontSize: 13 }}>{ strings.repliesScreen.noSummaryFields }</Typography> }
          { fields.map(renderFieldSummary) }
        </Stack>
      </Stack>
    );
  };

  const columnTemplate = `${managementFields.map(() => "minmax(160px, 1fr)").join(" ")} ${AuthUtils.isSystemAdmin(keycloak) ? "48px" : ""}`;

  return (
    <Stack flex={ 1 } minWidth={ 0 }>
      <Tabs
        value={ selectedTab }
        onChange={ (_, value) => setSelectedTab(value) }
        aria-label={ strings.repliesScreen.viewSelector }
        sx={{ borderBottom: "1px solid #e2e5e3", mb: 2.5 }}
      >
        <Tab
          label={ strings.repliesScreen.summaryTab }
          sx={{
            fontSize: 13, fontWeight: 600, minHeight: 44
          }}
        />
        <Tab
          label={ strings.repliesScreen.repliesTab }
          sx={{
            fontSize: 13, fontWeight: 600, minHeight: 44
          }}
        />
      </Tabs>
      { selectedTab === 0 ? renderSummary() : (
        <>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={ 1 }
            useFlexGap
            flexWrap="wrap"
            alignItems={{ xs: "stretch", md: "center" }}
            mb={ 2.25 }
          >
            <Stack direction="row" alignItems="center" spacing={ 1 }>
              <Typography
                sx={{
                  color: "#4d4d4d",
                  fontSize: 13
                }}
              >
                { strings.repliesScreen.selectorShowOpen }
              </Typography>
              <Switch
                checked={ showAllReplies }
                onChange={ event => setShowAllReplies(event.target.checked) }
                inputProps={{ "aria-label": strings.repliesScreen.selectorShowAll }}
              />
              <Typography
                sx={{
                  color: "#4d4d4d",
                  fontSize: 13
                }}
              >
                { strings.repliesScreen.selectorShowAll }
              </Typography>
            </Stack>
            <Feature
              feature={ FeatureType.EXCEL_EXPORT }
              title={ strings.features.excelExport.title }
              description={ strings.features.excelExport.description }
              strategy={ FeatureStrategy.HIDE }
            >
              <Button
                variant="outlined"
                onClick={ onExportClick }
                sx={{
                  ml: { md: "auto !important" },
                  borderColor: "#d9dedb",
                  color: "#4d4d4d",
                  fontSize: 13,
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.75
                }}
              >
                { strings.repliesScreen.export }
              </Button>
            </Feature>
          </Stack>
          <Paper
            variant="outlined"
            sx={{
              borderColor: "#e2e5e3",
              borderRadius: 1,
              overflow: "hidden"
            }}
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: { xs: 680, sm: 0 } }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: columnTemplate,
                    gap: 2,
                    px: 2.25,
                    py: 1.25,
                    backgroundColor: "#fafbfa",
                    borderBottom: "1px solid #e2e5e3",
                    color: "#6e7472",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.7px",
                    textTransform: "uppercase"
                  }}
                >
                  { managementFields.map(field => <Box key={ field.name }>{ field.title }</Box>) }
                  { AuthUtils.isSystemAdmin(keycloak) && <Box/> }
                </Box>
                { loading && <Box display="flex" justifyContent="center" p={ 4 }><CircularProgress size={ 28 }/></Box> }
                { !loading && !rows.length &&
                <Typography
                  sx={{
                    color: "#8a8f8c",
                    fontSize: 13,
                    p: 2.25
                  }}
                >
                  { strings.repliesScreen.noReplies }
                </Typography>
                }
                { !loading && rows.map(row => (
                  <Box
                    key={ row.id }
                    onClick={ () => openReply(row.id) }
                    sx={{
                      display: "grid",
                      gridTemplateColumns: columnTemplate,
                      gap: 2,
                      alignItems: "center",
                      cursor: AuthUtils.isMetaformManager(keycloak) ? "pointer" : "default",
                      px: 2.25,
                      py: 1.25,
                      borderTop: "1px solid #f0f2f1",
                      "&:hover": AuthUtils.isMetaformManager(keycloak) ? { backgroundColor: "#f8faf8" } : undefined
                    }}
                  >
                    { managementFields.map(field => {
                      const fieldName = field.name || "";
                      const value = row[fieldName];
                      let content = value || "-";
                      if (fieldName === STATUS_FIELD_NAME) {
                        content = LocalizationUtils.getLocalizedStatusOfReply(row.replyStatus as ReplyStatus);
                      } else if (DATETIME_META_FIELDS.includes(fieldName) && value) {
                        content = moment(value).format("LLL");
                      }
                      return (
                        <Typography
                          key={ fieldName }
                          sx={{
                            color: "#4d4d4d",
                            fontSize: 13,
                            lineHeight: "20px"
                          }}
                        >
                          { content }
                        </Typography>
                      );
                    }) }
                    { AuthUtils.isSystemAdmin(keycloak) &&
                    <Box textAlign="right">
                      <IconButton
                        aria-label={ strings.generic.delete }
                        size="small"
                        onClick={ event => {
                          event.stopPropagation();
                          setDeletableReplyId(row.id);
                        } }
                        sx={{ color: "#bf3f32" }}
                      >
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </Box>
                    }
                  </Box>
                )) }
              </Box>
            </Box>
            <TablePagination
              component="div"
              count={ totalResults }
              page={ page }
              rowsPerPage={ resultsPerPage }
              rowsPerPageOptions={ [resultsPerPage] }
              onPageChange={ (_, nextPage) => setPage(nextPage) }
            />
          </Paper>
          { renderDeleteReplyConfirm() }
        </>
      ) }
    </Stack>
  );
};

export default FormRepliesScreen;